/**
 * GA4 custom event tracking for fuguelab.com
 * Tracks Calendly clicks, form submissions, and outbound links.
 */

// `window.gtag` is typed by @gjl/analytics's ambient `Gtag` global, which is in
// the compile via @gjl/site-kit. A local Window.gtag declaration here would
// conflict with it (merged-global types must be identical), so we rely on theirs.

function track(eventName: string, params: Record<string, unknown>) {
  window.gtag?.('event', eventName, params)
}

/** Track when a visitor clicks a "book a call" CTA (routes to the /book scheduler) */
export function trackCalendlyClick(source: string) {
  track('booking_cta_click', {
    link_url: '/book',
    event_category: 'booking',
    source,
  })
}

/** Track when the self-hosted booking widget loads (visitor sees available times) */
export function trackBookingView(bookingType: string) {
  track('booking_widget_view', {
    event_category: 'booking',
    booking_type: bookingType,
  })
}

/** Track when someone completes a booking via the self-hosted widget */
export function trackBookingComplete(bookingType: string) {
  track('booking_complete', {
    event_category: 'conversion',
    booking_type: bookingType,
    source: window.location.pathname,
  })
}

/** Track contact form submission */
export function trackFormSubmit(formName: string, email: string) {
  track('form_submit', {
    form_name: formName,
    event_category: 'lead',
    // Don't send PII to GA4, just that a submission happened
    has_email: !!email,
  })
}

/** Track when a visitor views a pricing page (packages or care plan) */
export function trackPricingView(source: string) {
  track('pricing_view', {
    event_category: 'engagement',
    source,
  })
}

/** Track when a visitor views a specific portfolio item */
export function trackPortfolioView(slug: string) {
  track('portfolio_view', {
    event_category: 'engagement',
    portfolio_slug: slug,
  })
}

/** Track page engagement (scroll depth + time on page) */
export function trackPageEngagement(page: string, timeOnPageSeconds: number, scrollDepthPercent: number) {
  track('page_engagement', {
    event_category: 'engagement',
    page_path: page,
    time_on_page_seconds: Math.round(timeOnPageSeconds),
    scroll_depth_percent: scrollDepthPercent,
  })
}

/** Track return visits (same device, localStorage-based) */
export function trackReturnVisit(visitNumber: number) {
  track('return_visit', {
    event_category: 'engagement',
    visit_number: visitNumber,
  })
}

/** Track when a visitor clicks the "Tell Us About Your Project" CTA on the welcome page */
export function trackGetStartedClick(source: string) {
  track('get_started_click', {
    event_category: 'lead',
    source,
  })
}

/** Track when the intake form pre-fills orgType from UTM params (outreach attribution) */
export function trackFormPrefill(utmSlug: string) {
  track('form_prefill', {
    event_category: 'lead',
    utm_content: utmSlug,
  })
}

/** Track when a visitor requests a free website audit from the welcome page */
export function trackAuditRequest(source: string, email?: string, orgName?: string) {
  track('audit_request', {
    event_category: 'lead',
    source,
    ...(email && { email }),
    ...(orgName && { org_name: orgName }),
  })
}

/**
 * Global click handler for Calendly outbound links.
 * Attach once in main.tsx — catches all <a> clicks to calendly.com.
 */
export function initOutboundClickTracking() {
  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
    if (!link) return

    const href = link.href
    if (href.includes('calendly.com')) {
      trackCalendlyClick(window.location.pathname)
    }
  })
}
